/*
#*===================================================================
#*
#* Licensed Materials - Property of Zuum Transportation Inc.
#*
#* Copyright Zuum 2015-2022. All Rights Reserved.
#*
#*===================================================================
*/
import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, throwError } from 'rxjs';

import { NGXLogger } from 'ngx-logger';

import { ApiResponse } from './../models/api-response.model';
import { ApiService } from './../../services/api.service';
import { ClientDocument } from './../models/client-document.model';
import { ClientDocumentType } from '@core/enums';
import { GcpUploadResponse } from './../models/upload-file.model';
import { environment } from 'environments/environment';
import { AuthService } from './auth.service';
import { UtilsService } from './utils.service';
import { SNACKBAR_NOTIFICATION_TYPE } from '@core/constants';

@Injectable({ providedIn: 'root' })
export class ClientDocumentService {
  constructor(
    private httpBackend: HttpBackend,
    private authService: AuthService,
    private http: HttpClient,
    private apiService: ApiService,
    private utilsService: UtilsService,
    private logger: NGXLogger
  ) {}

  getDocuments(clientId: number): Observable<ApiResponse<ClientDocument[]>> {
    return this.http.get<ApiResponse<ClientDocument[]>>(`${this.apiService.superAdminApi}/client-document/${clientId}`);
  }

  getClientDocumentByType(clientId: number, type: ClientDocumentType): Observable<ApiResponse<ClientDocument>> {
    return this.http.get<ApiResponse<ClientDocument>>(
      `${this.apiService.superAdminApi}/client-document/${clientId}/${type}`
    );
  }

  uploadDocument(document: Partial<ClientDocument>): Observable<ApiResponse<ClientDocument>> {
    return this.http.post<ApiResponse<ClientDocument>>(`${this.apiService.superAdminApi}/client-document`, document);
  }

  updateDocument(document: Partial<ClientDocument>): Observable<ApiResponse<ClientDocument>> {
    return this.http.put<ApiResponse<ClientDocument>>(
      `${this.apiService.superAdminApi}/client-document/${document.id}`,
      document
    );
  }

  deleteDocument(documentId: number): Observable<ApiResponse<number>> {
    return this.http.delete<ApiResponse<number>>(`${this.apiService.superAdminApi}/client-document/${documentId}`);
  }

  sendEmailWithDocument(companyId: string, params: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiService.companies}/${companyId}/documents/email`, params);
  }

  uploadDocumentToGCP(
    file: File,
    fileName?: string,
    saveOnPrivate = environment.isDev || environment.isQA
  ): Observable<ApiResponse<GcpUploadResponse>> {
    if (!file) {
      return throwError('No file provided. Please provide a file to upload.');
    }

    const formData = new FormData();
    formData.append('file', file, fileName || file.name);

    if (saveOnPrivate) {
      formData.append('saveOnPrivate', 'true');
    }

    return this.http.post<ApiResponse<GcpUploadResponse>>(`${this.apiService.storageApi}/upload-file`, formData);
  }

  getSignedUrl(url: string): Observable<string | null> {
    if (environment.isStage || environment.isPROD) {
      return of(url);
    }

    const httpClient = new HttpClient(this.httpBackend);
    const { authToken } = this.authService;

    return httpClient
      .get<ApiResponse<{ url: string }>>(`${environment.storageApiUrl}/generate-signed-url`, {
        params: { url: new URL(url).toString() },
        ...((authToken && { headers: { Authorization: `Bearer ${authToken}` } }) || {}),
      })
      .pipe(
        map((res: ApiResponse<{ url: string }>) => (res?.result?.success ? res.data.url : null)),
        catchError((error: HttpErrorResponse) => throwError(error))
      );
  }

  // Download document file
  openInPreview(url: string): void {
    if (!url) {
      this.utilsService.showNotification('The Document URL is missing.', SNACKBAR_NOTIFICATION_TYPE.ERROR);
      return;
    }

    // Extract file extension using a regular expression
    const match = url.match(/\.([a-zA-Z0-9]+)(\?.*)?$/);
    const fileType = match ? match[1].toLowerCase() : null;

    if (!fileType) {
      this.logger.error('Unable to determine file type from URL:', url);
      return;
    }

    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileType);
    const isPdf = fileType === 'pdf';
    const isXlsx = fileType === 'xlsx';

    if (isImage || isPdf) {
      this.downloadFile(url);
    } else if (isXlsx) {
      this.downloadFile(url, false);
    } else {
      this.logger.error('Unsupported file type:', fileType);
    }
  }

  downloadFile(url: string, isPreview = true, filename?: string): void {
    const protocol = document.location.protocol;
    const host = document.location.host;

    // Create a URL object
    const linkURL = isPreview ? new URL(`${protocol}//${host}/doc-preview`) : new URL(url);

    if (isPreview) {
      // Use URLSearchParams for adding query parameters to the URL
      linkURL.searchParams.append('url', url);
    }

    // Create an anchor element
    const link = document.createElement('a');
    link.href = linkURL.toString(); // Convert the URL object to string

    if (isPreview) {
      link.target = '_blank';
    }

    if (filename) {
      link.download = filename;
    }

    // Append the link to the body, click the link to download/preview the file, and then remove the link from the body
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
